import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { EventLayout } from "@/components/EventLayout";
import { useEvent } from "@/context/EventContext";
import { startFeatureCheckout } from "@/components/FeatureGate";
import { Check, Sparkles, Users, Armchair, Layers, Gift } from "lucide-react";

const ICONS = { guests: Users, planning: Layers, seating: Armchair };
const HL_BUNDLE = { tasks: "planning", budget: "planning", vendors: "planning", gallery: "guests", thankyou: "guests", invites: "guests", seating: "seating" };

export default function Pricing() {
  const { features, eapi, eventId } = useEvent();
  const catalog = features?.catalog || [];
  const unlocked = features?.unlocked || [];
  const currency = features?.currency || "USD";
  const published = ["invites", "thankyou", "gallery"].every((k) => unlocked.includes(k));

  const allBundle = catalog.find((f) => f.key === "all");
  const bundles = catalog.filter((f) => f.key !== "all");
  const allUnlocked = unlocked.includes("all");
  const separateTotal = bundles.reduce((sum, f) => sum + (f.price || 0), 0);

  const [params] = useSearchParams();
  const highlight = params.get("highlight");
  useEffect(() => {
    if (!highlight || !features) return;
    const key = HL_BUNDLE[highlight] || highlight;
    const el = document.querySelector(`[data-testid="plan-card-${key}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-[#8C9A86]", "ring-offset-2", "ring-offset-[#FDFBF7]");
    const t = setTimeout(() => el.classList.remove("ring-2", "ring-[#8C9A86]", "ring-offset-2", "ring-offset-[#FDFBF7]"), 3000);
    return () => clearTimeout(t);
  }, [highlight, features]);

  return (
    <EventLayout>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 lg:py-12" data-testid="pricing-page">
        <p className="overline">Add-ons</p>
        <h1 className="font-serif text-4xl tracking-tight">Build your perfect toolkit</h1>
        <p className="text-stone-500 mt-3 max-w-2xl">Creating your event, building your guest list and your gift registry are always free. Unlock the bundles below only when you need them. These are one-time payments with no subscription. Your public event page goes live automatically once the Guest List & Invitations bundle (Invitations, Thank-You and Gallery) is unlocked.</p>

        <div className={`mt-6 rounded-xl px-5 py-4 text-sm ${published ? "bg-[#8C9A86]/12 text-[#5f6b59]" : "bg-white border border-[#E8E6E1] text-stone-500"}`} data-testid="publish-status">
          {published ? "✓ Your event page is published and visible to guests." : "Your event is saved but not yet published. Unlock the Guest List & Invitations bundle (or Unlock Everything) to send invites and go live."}
        </div>
        {features?.free_unlocked && (
          <div className="mt-4 rounded-xl bg-[#8C9A86]/12 text-[#5f6b59] px-5 py-4 text-sm flex items-center gap-2" data-testid="free-unlock-banner">
            <Sparkles size={16} /> Everything is unlocked free on your account, enjoy every feature at no cost.
          </div>
        )}

        {allBundle && (
          <div data-testid="plan-card-all" className={`mt-8 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6 ${allUnlocked ? "bg-[#8C9A86]/10 border border-[#8C9A86]/40" : "bg-[#2C2A28] text-white"}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-[11px] px-2.5 py-1 rounded-full ${allUnlocked ? "bg-[#8C9A86] text-white" : "bg-white/15 text-white"}`}>Best value</span>
                {!allUnlocked && separateTotal > allBundle.price && <span className="text-[11px] text-[#D4C5B3]">Save vs buying separately (${separateTotal.toFixed(2)})</span>}
              </div>
              <h2 className={`font-serif text-3xl ${allUnlocked ? "text-stone-800" : "text-white"}`}>{allBundle.name}</h2>
              <p className={`text-sm mt-2 max-w-xl ${allUnlocked ? "text-stone-500" : "text-white/70"}`}>{allBundle.desc}</p>
            </div>
            <div className="text-center sm:text-right">
              <div className={`font-serif text-4xl ${allUnlocked ? "text-stone-800" : "text-white"}`} data-testid="price-all">${allBundle.price}<span className={`text-xs ml-1 ${allUnlocked ? "text-stone-400" : "text-white/50"}`}>{currency}</span></div>
              <div className="mt-4">
                {allUnlocked
                  ? <div className="rounded-full bg-[#8C9A86]/15 text-[#5f6b59] px-6 py-2.5 text-sm flex items-center justify-center gap-1.5" data-testid="unlocked-all"><Check size={14} /> Everything unlocked</div>
                  : <button onClick={() => startFeatureCheckout(eapi, eventId, "all")} data-testid="buy-all-btn" className="rounded-full bg-white text-stone-900 px-7 py-3 text-sm font-medium hover:bg-[#D4C5B3] transition-colors whitespace-nowrap flex items-center gap-2"><Sparkles size={15} /> Unlock everything for ${allBundle.price} {currency}</button>}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 my-8">
          <div className="h-px bg-[#E8E6E1] flex-1" />
          <span className="text-xs text-stone-400 uppercase tracking-wider">Or pick individual bundles</span>
          <div className="h-px bg-[#E8E6E1] flex-1" />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {bundles.map((f) => {
            const Icon = ICONS[f.key] || Sparkles;
            const isUnlocked = unlocked.includes(f.key) || allUnlocked;
            const priceLabel = `$${f.price} ${currency}`;
            return (
              <div key={f.key} data-testid={`plan-card-${f.key}`} className={`rounded-xl border p-6 flex flex-col ${isUnlocked ? "bg-[#8C9A86]/8 border-[#8C9A86]/40" : "bg-white border-[#E8E6E1]"}`}>
                <div className="flex items-center justify-between">
                  <div className={`w-11 h-11 rounded-full grid place-items-center ${isUnlocked ? "bg-[#8C9A86] text-white" : "bg-[#8C9A86]/12 text-[#8C9A86]"}`}><Icon size={19} strokeWidth={1.6} /></div>
                  <span className="font-serif text-3xl text-stone-800" data-testid={`price-${f.key}`}>${f.price}<span className="text-xs text-stone-400 ml-1">{currency}</span></span>
                </div>
                <h3 className="font-serif text-2xl mt-4">{f.name}</h3>
                <p className="text-sm text-stone-500 mt-1.5 flex-1">{f.desc}</p>
                <div className="mt-5">
                  {isUnlocked
                    ? <div className="w-full rounded-full bg-[#8C9A86]/15 text-[#5f6b59] py-2.5 text-sm text-center flex items-center justify-center gap-1.5" data-testid={`unlocked-${f.key}`}><Check size={14} /> {allUnlocked && !unlocked.includes(f.key) ? "Included in Everything" : "Added to your event"}</div>
                    : <button onClick={() => startFeatureCheckout(eapi, eventId, f.key)} data-testid={`buy-${f.key}-btn`} className="w-full rounded-full bg-[#2C2A28] text-white py-2.5 text-sm hover:bg-[#8C9A86] transition-colors">Unlock for {priceLabel}</button>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 rounded-xl border border-[#E8E6E1] bg-white px-5 py-4 flex items-center gap-3" data-testid="registry-free-note">
          <div className="w-9 h-9 rounded-full bg-[#8C9A86]/12 grid place-items-center text-[#8C9A86]"><Gift size={16} /></div>
          <p className="text-sm text-stone-600"><span className="font-medium text-stone-800">Gift Registry</span> is always free, included with every event.</p>
        </div>

        <p className="text-xs text-stone-400 mt-6 text-center">Secure one-time payments via Stripe · no subscription</p>
      </div>
    </EventLayout>
  );
}
